// Fixture OperatorSummary for the real Pete's Panels job
// "Loom Review and Updates/Feedback" (ai_jobs e2c2c33f). Hand-written from
// the agents' spec_body and report_body (see petesLoomBodies.ts): the same
// facts, in plain language, short. The approval stage is what the approval
// report would say once the PR merges; the live job is build_ready today.
//
// Length rules the agents will be held to in PR2: request <= 25 words,
// action <= 45, each build-check field <= 40, need-to-know bullet <= 30.

import type { AiJob } from '../../../../lib/supabase.types';
import type { OperatorSummary } from '../../../../lib/aiJobSummary';
import { PETES_LOOM_REPORT_BODY, PETES_LOOM_SPEC_BODY } from './petesLoomBodies';

export const PETES_LOOM_JOB_ID = 'e2c2c33f-4940-4851-bb80-550ca5ebc269';
export const PETES_LOOM_TASK_ID = '7be25ad1-51b6-4714-a702-6c72332e0ce1';
export const PETES_LOOM_CARD_ID = '6aa18343fb37b0bb4c39082f';
export const PETES_LOOM_CLIENT_NAME = "Pete's Panels";

const PREVIEW = 'https://deploy-preview-53--petes-panels-backend.netlify.app';

export const PETES_LOOM_SUMMARY: OperatorSummary = {
  spec: {
    title: 'Loom Review and Updates/Feedback',
    greeting: "Hey Kyle, here's what Pete asked for from Pete's Panels. Eight things off one Loom: four to build, four he just needs answered. Nothing scary in here.",
    sources: [
      {
        id: 'trello',
        kind: 'trello',
        label: 'Trello card',
        url: 'https://trello.com/c/u6wuwShI',
      },
      {
        id: 'loom',
        kind: 'loom',
        label: 'Loom · Reviewing Bonuses, Profit Margins, and Payments · 8:10 · Pete Ortega',
        url: 'https://www.loom.com/share/28be70de34eb448ab8b8deae90056caf',
      },
    ],
    requests: [
      {
        id: 'R1',
        source: 'loom',
        kind: 'build',
        text: "Show each rep's profit margin on the Bonus leaderboard when you hover their name, e.g. 8,664 into 29,675 is about 26%.",
      },
      {
        id: 'R2',
        source: 'loom',
        kind: 'question',
        risks: ['payments'],
        text: 'If Pete clicks Mark Paid on his own $19k bonus instead of Approve, does that mess with QuickBooks?',
      },
      {
        id: 'R3',
        source: 'loom',
        kind: 'bug',
        risks: ['prod-data'],
        text: 'Brittany shows up twice on the Games board. It should be Jeremiah, Pete, Sonja, Ali, Aaron and Brittany, once each.',
      },
      {
        id: 'R4',
        source: 'loom',
        kind: 'question',
        text: 'How does Pete add new people to the Games roster himself?',
      },
      {
        id: 'R5',
        source: 'loom',
        kind: 'question',
        text: 'When he types new call counts, does the box add to the saved number or replace it? He does not want to reset anything.',
      },
      {
        id: 'R6',
        source: 'loom',
        kind: 'question',
        text: 'The phone system records calls. Anything useful we could do with that later? He answered himself: probably not.',
      },
      {
        id: 'R7',
        source: 'loom',
        kind: 'build',
        text: 'Colour the ready-to-go draft bonus reports pink so he can tell them from the ones still being filled in.',
      },
      {
        id: 'R8',
        source: 'loom',
        kind: 'bug',
        risks: ['payments'],
        text: "Brittany's QuickBooks bill says her payment info is missing, even though she has been paid before. Jeremiah's bill was fine.",
      },
    ],
    actions: [
      {
        request: 'R1',
        text: 'Add a bonus-rate line to the hover tooltip (bonuses divided by income), a matching column in The Receipts table, and an average for the whole window. Zero income shows a dash, never a broken number.',
      },
      {
        request: 'R2',
        text: 'Answer: no. Mark Paid only updates this app and never talks to QuickBooks. Approve is what opens a bill. He can hit Mark Paid on his HEI Civil row today.',
      },
      {
        request: 'R3',
        text: "Add a 'Who's on the board' picker in Game settings instead of deleting accounts. The board reads that list; standings keep everyone's history. The extra Brittany is left unticked.",
      },
      {
        request: 'R4',
        text: "Answer: invite them in Settings then Users, then tick them under 'Who's on the board'. Both are his own screens once R3 ships.",
      },
      {
        request: 'R5',
        text: 'Answer: it replaces. The box always holds the period total so far, so he types 11, not 2. Blank boxes are skipped. The on-screen sentence is rewritten to say exactly that.',
      },
      {
        request: 'R6',
        text: "Parked. Pete said 'probably not' himself. Call recordings would need a phone-system integration this app does not have; it gets its own card if he ever wants it.",
      },
      {
        request: 'R7',
        text: "Add a 'Ready to go' toggle on drafts. Ready rows turn pink, get a Ready badge, sort to the top, and the Drafts header counts them. White stays white.",
      },
      {
        request: 'R8',
        text: "Diagnosis: QuickBooks quietly created a second vendor, 'Brittany Wellington (Rep)', with no bank details. The approve message will now announce any brand-new vendor so this is caught early. Adding her ACH is Ali's job in QuickBooks.",
      },
    ],
    repo: {
      name: 'eczzz/Petes-Panels-backend',
      note: 'The admin app at admin.petespanels.com. Nothing else is touched.',
    },
    db: {
      prod: 'vekkptpabhizqybjvydr',
      preview: 'djhuewpximhwyarlchhh',
      note: 'One additive migration; preview on build, production at approval.',
    },
    need_to_know: [
      'Preview has no QuickBooks credentials, so the new-vendor message (R8) cannot be tried live. It is checked by reading the code, not by approving a real report.',
      "The roster backfill also put Kyle and Katie on Pete's Games board. He unticks the two Bullfinch accounts himself, in one click.",
      'The migration reaches production only at approval. Until then nothing on the live site changes.',
    ],
    decisions: [
      {
        id: 'D1',
        question: "Pete says profit margin; the number is bonuses ÷ income. Label it 'Bonus rate' or 'Margin'?",
        options: ['Bonus rate', 'Margin'],
        recommendation: 'Bonus rate',
        why: "It says what the number is. Margin means profit after costs, and Pete's accountant would read it that way.",
        answer: null,
      },
    ],
    approach: [
      "Take the eight asks in Pete's order: four are code, four are answers he is owed.",
      'Leaderboard: reuse the income and bonus figures already on screen; no new data.',
      'Games board: one new on-the-board flag per user plus a picker, so no account is deleted or merged.',
      'Drafts: two small fields record who marked a report ready and when; the pink follows that.',
      'Approve flow: report back whenever QuickBooks had to create a vendor, so surprises show up early.',
    ],
    parked: [
      "Duplicate Brittany and Kyle accounts. Which id owns Brittany's live $216 bonus is unanswered, so nothing is merged or deleted. Own card.",
      'Recorded calls (R6). No integration exists and Pete does not need it now.',
      "ACH on the new 'Brittany Wellington (Rep)' vendor. Ali adds it inside QuickBooks.",
    ],
  },
  build: {
    pr_url: 'https://github.com/eczzz/Petes-Panels-backend/pull/53',
    preview_url: PREVIEW,
    db_written: 'preview djhuewpximhwyarlchhh only (migration 20260909120000, drift 99 = 99)',
    deviations: ["Also shipped optional item E 'Keep in business' (no SQL). Strike it on approval if unwanted."],
    revision: {
      note: 'The bonus rate should be a percent, not a decimal. And show the average under the column.',
      done: [
        'Bonus rate now reads as a percent everywhere: tooltip, The Receipts column and its average.',
        'The Receipts shows the average rate under the column for the dates picked.',
      ],
    },
    checks: [
      {
        request: 'R1',
        before: 'Hovering a rep showed income and bonuses only; no rate anywhere.',
        after: "Tooltip adds '21.9% of income paid out in bonuses'. The Receipts gets a Bonus rate column and an average for the window.",
        how_to_check: 'Leaderboard, Bonuses tab, Last 30 Days. Hover Pete and Jeremiah; the column and the average should agree with income and bonuses.',
        url: `${PREVIEW}/leaderboard`,
      },
      {
        request: 'R3',
        before: 'The Games board listed every account, so both Brittany logins appeared.',
        after: "One Brittany. Game settings has a 'Who's on the board' checklist, saved with the prize amounts.",
        how_to_check: 'Leaderboard, Games tab. Untick someone: they leave the calls table but stay in the standings.',
        url: `${PREVIEW}/leaderboard`,
      },
      {
        request: 'R5',
        before: "The sentence said 'Saving replaces that person's count', which read like a reset.",
        after: "Copy now says to enter each person's total for the period, and a 'replaces 9' hint appears beside a changed box.",
        how_to_check: 'Leaderboard, Games tab. Change a call count and read the hint next to the box.',
        url: `${PREVIEW}/leaderboard`,
      },
      {
        request: 'R7',
        before: 'All drafts looked the same. Nothing recorded that one was ready.',
        after: 'Mark ready turns the row pink with a Ready badge, sorts it up, and the Drafts header counts ready drafts. Not yet turns it back.',
        how_to_check: 'Bonuses, Drafts. Mark one ready, then Not yet. Check dark mode too.',
        url: `${PREVIEW}/bonuses`,
      },
      {
        request: 'R8',
        before: 'A brand-new QuickBooks vendor was created silently, with no bank details on file.',
        after: 'The approve message names the new vendor and says to add payment info before scheduling payment.',
        how_to_check: 'Cannot run live on preview (no QuickBooks there). Read the message text in the PR diff.',
      },
    ],
  },
  approval: {
    live_url: 'https://admin.petespanels.com',
    client_note: [
      'Hey Pete! 🎉 Everything from your Loom is live.',
      '',
      "### What's new",
      '',
      '- **Bonus rate** — hover a name on the Bonus leaderboard to see how much of what they brought in went back out as bonuses. There’s a column in The Receipts too, with an average.',
      '- **Ready drafts pop** — mark a draft Ready to go and it turns pink, jumps to the top, and the header counts them.',
      "- **One Brittany** on the Games board, plus a Who's on the board list so you can add or remove players.",
      "- **A new Settings tab** holds all of it, so the leaderboard isn't such a long scroll.",
      '',
      '### Your questions',
      '',
      '- **Does Mark Paid hit QuickBooks?** No, only Approve does. Safe on your own bonus.',
      '- **Adding someone?** Invite them under Settings → Users, then tick them on the board.',
      "- **The call box replaces, it doesn't add.** Type the period total, so 11, not 2. Blank boxes skip that person.",
      '',
      '### One for Ali',
      '',
      'QuickBooks made Brittany a second vendor record with no bank details. Ali can add her ACH there, and approvals now warn you when a new vendor is created.',
      '',
      'Take a look 👉 [admin.petespanels.com](https://admin.petespanels.com/leaderboard)',
    ].join('\n'),
    confirmed: [
      { request: 'R1', status: 'live' },
      { request: 'R2', status: 'live', note: 'answered in the note' },
      { request: 'R3', status: 'live' },
      { request: 'R4', status: 'live', note: 'answered in the note' },
      { request: 'R5', status: 'live', note: 'answered in the note' },
      { request: 'R6', status: 'not-live', note: 'parked, its own card if wanted' },
      { request: 'R7', status: 'live' },
      { request: 'R8', status: 'partial', note: 'message is live; ACH still needs Ali in QuickBooks' },
    ],
    follow_ups: [
      "Ali adds ACH to 'Brittany Wellington (Rep)' in QuickBooks, or merges it into her existing vendor record.",
      "Pete unticks Kyle and Katie under 'Who's on the board'.",
      'Decide whether the two duplicate accounts get merged. Own card.',
      "Fix the CLI lock-paths bug before two Pete's Panels jobs ever run together.",
    ],
  },
};

/** Gallery-only sample of the approval markdown; the live job has none yet. */
const PETES_LOOM_APPROVAL_SAMPLE = `# Approval report: Loom Review and Updates/Feedback (Pete's Panels)

_Gallery sample. The real job is build_ready; this is what the approval report would say once PR #53 merges._

Job: \`${PETES_LOOM_JOB_ID}\`
Merged: https://github.com/eczzz/Petes-Panels-backend/pull/53 into \`main\`
Live: https://admin.petespanels.com

## Migration

Applied \`20260909120000\` to production \`vekkptpabhizqybjvydr\` with SQL tracking. Drift: 100 = 100, zero both directions. Roster backfill on production data picked one row per name.

## Live check

Bonus rate tooltip, Receipts column and Avg Bonus rate KPI present on \`/leaderboard\`. Games board shows one Brittany; Who's on the board lists the active users. Ready to go turns a draft pink on \`/bonuses\`. Approve toast text confirmed in the deployed bundle.

## Follow-ups

- Ali: ACH on "Brittany Wellington (Rep)" in QuickBooks.
- Pete: untick Kyle and Katie under Who's on the board.
- Duplicate accounts: own card.
`;

/** The real job row, minus fields the gallery overrides per stage. */
const PETES_LOOM_JOB_BASE: AiJob = {
  id: PETES_LOOM_JOB_ID,
  task_id: PETES_LOOM_TASK_ID,
  client_id: '1fa27d16-8fa6-461a-8c87-e76a1f818fda',
  card_id: PETES_LOOM_CARD_ID,
  status: 'build_ready',
  kind: 'fulfillment',
  task_type: 'general',
  title: 'Loom Review and Updates/Feedback',
  spec_body: PETES_LOOM_SPEC_BODY,
  report_body: PETES_LOOM_REPORT_BODY,
  approval_body: '',
  revision_note: '',
  handoff_notes: [],
  queue_note:
    'Enriched transcript attached\n\nAttachments:\n- [loom-28be70de-enriched.md](https://media.bullfinch.io/job-attachments/e2c2c33f-4940-4851-bb80-550ca5ebc269/1788975554729-loom-28be70de-enriched.md)',
  brief: {
    attachments: [
      {
        url: 'https://media.bullfinch.io/job-attachments/e2c2c33f-4940-4851-bb80-550ca5ebc269/1788975554729-loom-28be70de-enriched.md',
        name: 'loom-28be70de-enriched.md',
        size: 15310,
        type: 'text/markdown',
      },
    ],
  },
  auto_approve: false,
  version: 3,
  claimed_by: null,
  claimed_at: null,
  created_by: '23ccbdab-b62b-436a-8bbc-fd992c194529',
  created_at: '2026-09-09T11:39:13.202075-06:00',
  updated_at: '2026-09-09T12:02:42.919639-06:00',
  queue_position: 1,
  lock_reason: '',
  blocked_by_job_id: null,
  db_ref: null,
  parked_from: null,
  migration_version: null,
  model: 'anthropic/claude-opus-5',
  operator_summary: null,
};

export type PetesLoomGalleryStage = 'spec_ready' | 'build_ready' | 'complete';

/** A fake AiJob for the design gallery at one of three stages. No DB. */
export function petesLoomJobAt(stage: PetesLoomGalleryStage): AiJob {
  if (stage === 'spec_ready') {
    return {
      ...PETES_LOOM_JOB_BASE,
      status: 'spec_ready',
      version: 2,
      report_body: '',
      approval_body: '',
      updated_at: '2026-09-09T11:52:10.000000-06:00',
    };
  }
  if (stage === 'complete') {
    return {
      ...PETES_LOOM_JOB_BASE,
      status: 'complete',
      version: 4,
      approval_body: PETES_LOOM_APPROVAL_SAMPLE,
      migration_version: '20260909120000',
      updated_at: '2026-09-09T14:21:05.000000-06:00',
    };
  }
  return { ...PETES_LOOM_JOB_BASE };
}
